import React, {useContext, useEffect} from 'react';
import {useSearchParams} from 'react-router-dom';
import {Breadcrumb, ScreenDataTable, StatisticView} from '../components';
import {Screen} from '../types';
import {AppContext} from '../contexts/AppContext';
import {useScreenNavigation} from '../hooks/useScreenNavigation';

interface ScreenPageProps {
    screen: Screen;
    appId: string;
}

export const ScreenPage: React.FC<ScreenPageProps> = ({screen, appId}) => {
    const [params] = useSearchParams();
    const {selectedApplication} = useContext(AppContext);

    // Breadcrumb stack sync (clear on fresh PUSH, heal on POP/refresh) is
    // handled centrally in App.tsx (BreadcrumbNavigationSync).

    useEffect(() => {
        const appName = selectedApplication?.displayName || selectedApplication?.name || 'Flowy';
        document.title = `${appName} | ${screen.displayName}`;
        return () => {
            document.title = 'Flowy';
        };
    }, [screen, selectedApplication]);

    // Breadcrumb path must keep the current query string (userFilter,
    // pagination) so going back via breadcrumb restores the selected filter.
    const currentScreenPath = (): string => {
        const search = params.toString();
        return `/app/${appId}/screen/${screen.id}${search ? `?${search}` : ''}`;
    };

    const {handleOpenCreateForm, handleOpenUpdateForm, handleNavigateToScreen} =
        useScreenNavigation(appId, screen, {label: screen.displayName, path: currentScreenPath});

    return (
        <>
            <Breadcrumb currentLabel={screen.displayName} appId={appId} />
            <div className="page-body">
                <div className="container-fluid">
                    <section className="v5-display">
                        <div>
                            <h1>{screen.displayName}</h1>
                        </div>
                    </section>
                    {screen.style === 'statistic' ? (
                        <StatisticView screen={screen} />
                    ) : (
                        <ScreenDataTable
                            screen={screen}
                            onNavigateToScreen={handleNavigateToScreen}
                            onOpenUpdateForm={handleOpenUpdateForm}
                            onOpenCreateForm={handleOpenCreateForm}
                        />
                    )}
                </div>
            </div>
        </>
    );
};
